import React, { useState } from 'react';
import { SocialPlatform } from '../../../../types/socialMediaPlanner';
import { socialMediaContentService, ContentGenerationOptions } from '../../../../services/socialMediaContentService';
import toast from 'react-hot-toast';

interface ClaudeContentModalProps {
  platforms: SocialPlatform[];
  onClose: () => void;
  onContentGenerated: (content: string, hashtags?: string[]) => void;
  existingContent?: string;
}

/**
 * ClaudeContentModal Component
 * 
 * A modal for generating social media content using Claude Sonnet 3.7
 * Uses Tailwind CSS exclusively for styling, following project standards.
 */
const ClaudeContentModal: React.FC<ClaudeContentModalProps> = ({
  platforms,
  onClose,
  onContentGenerated,
  existingContent
}) => {
  // State for form fields
  const [topic, setTopic] = useState<string>('');
  const [targetAudience, setTargetAudience] = useState<string>('');
  const [contentType, setContentType] = useState<'post' | 'caption' | 'hashtags' | 'engagement'>('post');
  const [tone, setTone] = useState<'professional' | 'casual' | 'humorous' | 'inspirational' | 'educational'>('professional');
  const [contentLength, setContentLength] = useState<'short' | 'medium' | 'long'>('medium');
  const [includeHashtags, setIncludeHashtags] = useState<boolean>(true);
  const [includeEmojis, setIncludeEmojis] = useState<boolean>(true);
  const [includeQuestion, setIncludeQuestion] = useState<boolean>(false);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Validate essential fields
      if (!topic) {
        setError('Please provide a topic');
        setIsLoading(false);
        return;
      }
      
      if (platforms.length === 0) {
        setError('Please select at least one platform');
        setIsLoading(false);
        return;
      }
      
      // Create options object for content generation
      const options: ContentGenerationOptions = {
        contentType,
        topic,
        targetAudience: targetAudience || undefined,
        tone,
        platforms,
        contentLength,
        includeHashtags,
        includeEmojis,
        includeQuestion,
        existingContent: existingContent || undefined
      };
      
      // Call the service
      const result = await socialMediaContentService.generateContent(options);
      
      // Pass the generated content back to parent component
      onContentGenerated(result.content, result.suggestedHashtags);
      toast.success('Content generated with Claude AI');
      
      // Close the modal
      onClose();
    } catch (err) {
      console.error('Error generating content:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate content');
      toast.error('Failed to generate content');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[85vh] overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
              <svg className="w-8 h-8 mr-2 text-purple-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd"></path>
              </svg>
              Generate with Claude
            </h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4" role="alert">
              <p>{error}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 mb-4">
              {/* Content Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content Type</label>
                <div className="grid grid-cols-4 gap-2">
                  <button
                    type="button"
                    onClick={() => setContentType('post')}
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      contentType === 'post'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Post
                  </button>
                  <button
                    type="button"
                    onClick={() => setContentType('caption')}
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      contentType === 'caption'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Caption
                  </button>
                  <button
                    type="button"
                    onClick={() => setContentType('hashtags')}
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      contentType === 'hashtags'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Hashtags
                  </button>
                  <button
                    type="button"
                    onClick={() => setContentType('engagement')}
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      contentType === 'engagement'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Engagement
                  </button>
                </div>
              </div>
              
              {/* Topic */}
              <div>
                <label htmlFor="topic" className="block text-sm font-medium text-gray-700 mb-1">
                  Topic <span className="text-red-500">*</span>
                </label>
                <input
                  id="topic"
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="What should the content be about?"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                  required
                />
              </div>
              
              {/* Target Audience */}
              <div>
                <label htmlFor="audience" className="block text-sm font-medium text-gray-700 mb-1">
                  Target Audience
                </label>
                <input
                  id="audience"
                  type="text"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  placeholder="Who is your target audience? (e.g., small business owners, parents)"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                />
              </div>
              
              {/* Tone Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tone</label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <button
                    type="button"
                    onClick={() => setTone('professional')}
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      tone === 'professional'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Professional
                  </button>
                  <button
                    type="button"
                    onClick={() => setTone('casual')}
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      tone === 'casual'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Casual
                  </button>
                  <button
                    type="button"
                    onClick={() => setTone('humorous')}
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      tone === 'humorous'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Humorous
                  </button>
                  <button
                    type="button"
                    onClick={() => setTone('inspirational')}
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      tone === 'inspirational'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Inspirational
                  </button>
                  <button
                    type="button"
                    onClick={() => setTone('educational')}
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      tone === 'educational'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Educational
                  </button>
                </div>
              </div>
              
              {/* Content Length */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content Length</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setContentLength('short')}
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      contentLength === 'short'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Short
                  </button>
                  <button
                    type="button"
                    onClick={() => setContentLength('medium')}
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      contentLength === 'medium'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Medium
                  </button>
                  <button
                    type="button"
                    onClick={() => setContentLength('long')}
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      contentLength === 'long'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Long
                  </button>
                </div>
              </div>
              
              {/* Additional Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Options</label>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      id="include-hashtags"
                      type="checkbox"
                      checked={includeHashtags}
                      onChange={() => setIncludeHashtags(!includeHashtags)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="include-hashtags" className="ml-2 block text-sm text-gray-700">
                      Include hashtags
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="include-emojis"
                      type="checkbox"
                      checked={includeEmojis}
                      onChange={() => setIncludeEmojis(!includeEmojis)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="include-emojis" className="ml-2 block text-sm text-gray-700">
                      Include emojis
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="include-question"
                      type="checkbox"
                      checked={includeQuestion}
                      onChange={() => setIncludeQuestion(!includeQuestion)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="include-question" className="ml-2 block text-sm text-gray-700">
                      Include engaging question
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Selected Platforms */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Selected Platforms ({platforms.length})
              </label>
              <div className="flex flex-wrap gap-2">
                {platforms.map((platform) => (
                  <span 
                    key={platform}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {platform.charAt(0).toUpperCase() + platform.slice(1)}
                  </span>
                ))}
                {platforms.length === 0 && (
                  <span className="text-sm text-gray-500 italic">
                    No platforms selected. Please select at least one platform to generate content.
                  </span>
                )}
              </div>
            </div>
            
            {/* Buttons */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || platforms.length === 0 || !topic}
                className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                  ${isLoading || platforms.length === 0 || !topic
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  } 
                  flex items-center
                `}
              >
                {isLoading && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isLoading ? 'Generating...' : 'Generate with Claude'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClaudeContentModal;
